// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/mission-execution/MissionExecutionIntelligencePanel.jsx

import React, { useMemo, useState } from "react";
import { Button, StatusChip } from "../../design-system/components";

const PROMPTS = [
  "What should I work on first?",
  "What needs attention?",
  "What is blocked?",
  "What changed in my work?",
];

function clean(value) {
  return String(value ?? "").trim();
}

function normalizeRows(value) {
  return Array.isArray(value) ? value : [];
}

function formatConfidence(value) {
  const normalized = clean(value).toLowerCase();

  if (normalized === "high") return "High confidence";
  if (normalized === "medium") return "Medium confidence";
  if (normalized === "low") return "Low confidence";
  return "Advisory";
}

function confidenceTone(value) {
  const normalized = clean(value).toLowerCase();

  if (normalized === "high") return "success";
  if (normalized === "medium") return "informational";
  if (normalized === "low") return "warning";
  return "neutral";
}

function sourceLabel(source = {}) {
  const explicit = clean(source?.item_label);
  if (explicit) return explicit;

  return clean(source?.id || source?.type || "OASYS source")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function sourceDetail(source = {}) {
  const represented = Number(source?.item_count);
  const records = Number(source?.records);

  if (Number.isFinite(represented) && represented > 0) {
    return `${represented} represented`;
  }

  if (Number.isFinite(records) && records > 0) {
    return `${records} loaded`;
  }

  return "Grounded";
}

export default function MissionExecutionIntelligencePanel({
  question = "What should I work on first?",
  card = null,
  metadata = null,
  loading = false,
  error = "",
  onAsk,
}) {
  const [detailsOpen, setDetailsOpen] = useState(false);

  const recommendations = useMemo(
    () => normalizeRows(card?.recommended_actions).filter(Boolean).slice(0, 3),
    [card]
  );

  const evidence = useMemo(
    () =>
      normalizeRows(card?.evidence_used)
        .filter((item) => item && typeof item === "object")
        .slice(0, 12),
    [card]
  );

  const limitations = useMemo(
    () => normalizeRows(card?.known_limitations).filter(Boolean),
    [card]
  );

  const auditPersisted =
    metadata?.audit_persisted ??
    card?.metadata?.audit_persisted ??
    null;

  const hasResponse = Boolean(
    clean(card?.summary) ||
      clean(card?.reasoning) ||
      recommendations.length ||
      evidence.length
  );

  const handleAsk = (prompt) => {
    if (loading || typeof onAsk !== "function") return;
    onAsk(prompt);
  };

  return (
    <section
      className="mission-execution-intelligence"
      aria-labelledby="mission-execution-intelligence-title"
    >
      <div className="mission-execution-intelligence__header">
        <div className="mission-execution-intelligence__identity">
          <span className="mission-execution-intelligence__eyebrow">
            OASYS Intelligence
          </span>
          <h2 id="mission-execution-intelligence-title">
            Current execution intelligence
          </h2>
          <p>
            Grounded, advisory reasoning across the active work queue.
            Operator-controlled execution remains authoritative.
          </p>
        </div>

        <div className="mission-execution-intelligence__status">
          <StatusChip
            tone={confidenceTone(card?.confidence)}
            dot={hasResponse}
          >
            {hasResponse
              ? formatConfidence(card?.confidence)
              : "Awaiting context"}
          </StatusChip>

          {auditPersisted === true ? (
            <StatusChip tone="success">Audited</StatusChip>
          ) : null}

          <Button
            variant="ghost"
            size="small"
            onClick={() => setDetailsOpen((current) => !current)}
            aria-expanded={detailsOpen}
            className="mission-execution-intelligence__details-toggle"
          >
            {detailsOpen ? "Hide details" : "Details"}
          </Button>
        </div>
      </div>

      <div className="mission-execution-intelligence__prompts">
        {PROMPTS.map((prompt) => {
          const isActive = question === prompt;

          return (
            <Button
              key={prompt}
              variant={isActive ? "secondary" : "ghost"}
              size="small"
              disabled={loading}
              aria-pressed={isActive}
              onClick={() => handleAsk(prompt)}
              className={`mission-execution-intelligence__prompt${
                isActive ? " is-active" : ""
              }`}
            >
              {prompt}
            </Button>
          );
        })}
      </div>

      {error ? (
        <div className="mission-execution-intelligence__error" role="alert">
          <strong>Intelligence unavailable</strong>
          <span>{error}</span>
        </div>
      ) : null}

      {hasResponse ? (
        <div
          className="mission-execution-intelligence__body"
          aria-busy={loading}
        >
          <div className="mission-execution-intelligence__reasoning">
            <span className="mission-execution-intelligence__label">
              Current reasoning
            </span>
            <strong>
              {clean(card?.summary) || "Mission Execution context loaded."}
            </strong>
            {clean(card?.reasoning) ? <p>{card.reasoning}</p> : null}
          </div>

          {recommendations.length > 0 ? (
            <div className="mission-execution-intelligence__actions">
              <span className="mission-execution-intelligence__label">
                Recommended operator focus
              </span>
              <ol>
                {recommendations.map((action, index) => (
                  <li key={`${clean(action)}-${index}`}>
                    <span>{index + 1}</span>
                    <p>{clean(action)}</p>
                  </li>
                ))}
              </ol>
            </div>
          ) : null}
        </div>
      ) : (
        <div
          className="mission-execution-intelligence__initial"
          aria-busy={loading}
        >
          <strong>Preparing current execution intelligence.</strong>
          <span>
            OASYS is reading queue priority, blockers, validation pressure,
            ownership, and current execution state.
          </span>
        </div>
      )}

      {detailsOpen ? (
        <div className="mission-execution-intelligence__details">
          <div>
            <span className="mission-execution-intelligence__label">
              Grounding
            </span>

            {evidence.length > 0 ? (
              <div className="mission-execution-intelligence__sources">
                {evidence.map((source, index) => (
                  <div
                    key={`${clean(source?.type || source?.id) || "source"}-${index}`}
                    className="mission-execution-intelligence__source"
                  >
                    <strong>{sourceLabel(source)}</strong>
                    <span>{sourceDetail(source)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="mission-execution-intelligence__muted">
                Supporting OASYS execution records are attached to the current response.
              </p>
            )}
          </div>

          <div>
            <span className="mission-execution-intelligence__label">
              Known limitations
            </span>

            {limitations.length > 0 ? (
              <ul className="mission-execution-intelligence__limitations">
                {limitations.map((limitation, index) => (
                  <li key={`${clean(limitation)}-${index}`}>
                    {clean(limitation)}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mission-execution-intelligence__muted">
                No additional limitations were reported.
              </p>
            )}
          </div>
        </div>
      ) : null}
    </section>
  );
}
